import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "@/lib/services";
import Reveal from "@/components/Reveal";
import Seo, { LOCAL_BUSINESS_LD, breadcrumbLd } from "@/components/Seo";

const HIGHLIGHTS = {
  "bespoke-joinery": ["Hardwood windows & doors", "Panelling & wainscoting", "Handmade shelving"],
  "extensions-renovations": ["Single & double storey", "Loft conversions", "Kitchen remodels"],
  "decking-outdoor": ["Hardwood & composite", "Pergolas & garden rooms", "Fencing & gates"],
  "staircases": ["Oak, ash, walnut", "Steel & timber balustrades", "Building-reg compliant"],
  "fitted-furniture": ["Alcove units & wardrobes", "Media walls", "Home offices"],
  "general-building": ["Brickwork & pointing", "Roofing repairs", "Full refurbishments"],
};

export default function Services() {
  return (
    <div data-testid="services-page" className="pt-32 md:pt-40">
      <Seo
        title="Joinery Services in Sheffield | Bespoke Joinery, Extensions, Staircases & Fitted Furniture"
        description="Sheffield joinery services: bespoke joinery, extensions & renovations, decking, staircases, fitted furniture and general building work. Serving Sheffield, Rotherham, Chesterfield, Dronfield and Barnsley."
        path="/services"
        jsonLd={[
          LOCAL_BUSINESS_LD,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: SERVICES.map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "Service",
                name: s.title,
                description: s.short,
                areaServed: "Sheffield",
                provider: { "@id": "https://woodlandjoinery.com/#business" },
              },
            })),
          },
        ]}
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <Reveal>
          <p className="text-[11px] tracking-[0.35em] uppercase text-[#4A5550]">
            Our Services
          </p>
          <h1 className="mt-4 font-display text-5xl md:text-7xl lg:text-8xl text-[#1F3B2D] leading-[0.95] max-w-4xl">
            The full craft, <em className="text-[#C8A24B] not-italic font-light">from timber to turnkey.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#4A5550] leading-relaxed">
            Whether you need a single bespoke piece or a full home renovation,
            we bring the same craftsmanship, planning and finish to every job.
          </p>
        </Reveal>
      </section>

      <div className="border-t border-[#E5E0D8]">
        {SERVICES.map((s, i) => {
          const reverse = i % 2 === 1;
          return (
            <section
              key={s.slug}
              id={s.slug}
              data-testid={`service-section-${s.slug}`}
              className="border-b border-[#E5E0D8]"
            >
              <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
                <div
                  className={`grid md:grid-cols-12 gap-10 md:gap-16 items-center ${
                    reverse ? "md:[direction:rtl]" : ""
                  }`}
                >
                  <Reveal className={`md:col-span-6 ${reverse ? "md:[direction:ltr]" : ""}`}>
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase text-white bg-[#1F3B2D]/85 px-3 py-1">
                        {s.tag}
                      </span>
                    </div>
                  </Reveal>
                  <Reveal
                    delay={140}
                    className={`md:col-span-6 ${reverse ? "md:[direction:ltr]" : ""}`}
                  >
                    <p className="text-[11px] tracking-[0.35em] uppercase text-[#C8A24B]">
                      0{i + 1} / 0{SERVICES.length}
                    </p>
                    <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-[#1F3B2D] leading-tight">
                      {s.title}
                    </h2>
                    <p className="mt-6 text-lg text-[#4A5550] leading-relaxed">
                      {s.long}
                    </p>
                    <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                      {HIGHLIGHTS[s.slug].map((h) => (
                        <li
                          key={h}
                          className="flex items-start gap-3 text-[15px] text-[#1A201C]"
                        >
                          <Check className="h-4 w-4 text-[#C8A24B] mt-1 shrink-0" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      data-testid={`service-cta-${s.slug}`}
                      className="btn-primary mt-10"
                    >
                      Enquire About {s.title} <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </Reveal>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <section className="timber-grain text-white py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <span className="gold-rule" />
            <h2 className="mt-6 font-display text-4xl md:text-6xl leading-tight">
              Not sure where to start?
            </h2>
            <p className="mt-6 text-lg text-white/70 max-w-xl mx-auto">
              Send us a quick message with your idea. We’ll pop over, take a look,
              and put together a free, no-obligation quote.
            </p>
            <Link
              to="/contact"
              data-testid="services-bottom-cta"
              className="btn-secondary mt-10 border-[#C8A24B] text-white hover:bg-[#C8A24B] hover:text-white"
            >
              Get a Free Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
